import Jwt from 'jsonwebtoken';
import queryString from 'query-string';

import { getReturnURL } from '../config/sso.js';

import {
  getSchoolInfoKS,
  getSchoolSlugbyID,
  getSchoolSlugbyOrgCode,
  getSession,
  getssoLoginUrl,
  schoolValidToken,
  ssoExchange,
} from '../services/user.js';
import { alertError } from '../utils/edittools.js';
import { responseCode, responseData, responseMessage } from '../types/domain.js';
import type { SsoSiteConfig } from '../types/domain.js';
import type { ModelSaga, TypedModel } from './dva-types.js';

export { getReturnURL } from '../config/sso.js';

interface ExternalUserResult {
  readonly UserName: string;
  readonly RealName: string;
  UserType: string;
  readonly OrgCode?: string;
  readonly Email?: string;
  readonly QQ?: string;
  readonly ssoToken?: string;
}

interface ExternalUserResponse {
  readonly Code: number;
  readonly Result: ExternalUserResult;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isNullableOptionalString(value: unknown): value is string | null | undefined {
  return value === null || value === undefined || typeof value === 'string';
}

function isExternalUserResponse(value: unknown): value is ExternalUserResponse {
  if (!isRecord(value) || typeof value['Code'] !== 'number' || !isRecord(value['Result'])) {
    return false;
  }
  const result = value['Result'];
  return (
    typeof result['UserName'] === 'string' &&
    typeof result['RealName'] === 'string' &&
    typeof result['UserType'] === 'string' &&
    isOptionalString(result['OrgCode']) &&
    isOptionalString(result['Email']) &&
    isOptionalString(result['QQ']) &&
    isOptionalString(result['ssoToken'])
  );
}

function isSsoSiteConfig(value: unknown): value is SsoSiteConfig {
  return (
    isRecord(value) &&
    isNullableOptionalString(value['webRedirectURL']) &&
    isNullableOptionalString(value['adminRedirectURL'])
  );
}

function isSessionWithSso(value: unknown): value is {
  sso?: Readonly<Record<string, SsoSiteConfig>>;
  deploymentStyle?: string;
} {
  if (
    value instanceof Error ||
    !isRecord(value) ||
    (value['deploymentStyle'] !== undefined && typeof value['deploymentStyle'] !== 'string')
  ) {
    return false;
  }
  const sso = value['sso'];
  return (
    sso === undefined ||
    (isRecord(sso) && Object.values(sso).every((siteConfig) => isSsoSiteConfig(siteConfig)))
  );
}

interface SsoLoginPayload {
  readonly Code?: string;
  readonly field?: string;
  readonly deploymentSlug?: string;
  readonly schoolSlug?: string;
}

interface RetryPayload {
  readonly schoolSlug?: string;
  readonly field?: string;
  readonly deploymentSlug: string;
  readonly ssoSlug: string;
}

const model: TypedModel<Record<string, unknown>> = {
  namespace: 'sso',
  state: {},
  subscriptions: {
    setup({ dispatch, history }) {
      history.listen(({ pathname, search }) => {
        window.scrollTo(0, 0);
        if (pathname !== '/sso/validate' && pathname !== '/sso/validate/njyz') return;
        if (!search) return;
        const parsed = queryString.parse(search) as Record<string, string>;
        if (pathname === '/sso/validate/njyz') {
          dispatch({ type: 'njyzLogin', payload: parsed });
          return;
        }
        dispatch({ type: 'ssoLogin', payload: parsed });
      });
    },
  },
  effects: {
    *ssoLogin({ payload }, { put }): ModelSaga {
      const loginPayload = payload as SsoLoginPayload;
      const { Code, field, deploymentSlug = 'hxr.i51cy.com' } = loginPayload;
      let { schoolSlug } = loginPayload;
      const currentSession = yield getSession();
      const apiSSO = isSessionWithSso(currentSession) ? currentSession.sso : undefined;
      const deploymentStyle = isSessionWithSso(currentSession)
        ? currentSession.deploymentStyle
        : undefined;
      if (!apiSSO) {
        alertError('没有发现本站有单点登录配置，请检查跳转站点域名是否一致');
        return;
      }
      const sso = apiSSO[deploymentSlug];
      if (!sso) {
        alertError('请返回重试');
        return;
      }
      if (!Code) {
        yield put({
          type: 'retryLogin',
          payload: { schoolSlug, field, deploymentSlug, ssoSlug: window.location.host },
        });
        return;
      }

      // Code → Token → 用户信息的两步换取由 api-main 服务端完成，避免浏览器
      // 直连第三方 SSO 域名触发 CORS 拦截。
      const exchangeResult = yield ssoExchange({ deploymentSlug, Code });
      const userResult = responseData<unknown>(exchangeResult);
      if (!isExternalUserResponse(userResult) || userResult.Code !== 200) {
        yield put({
          type: 'retryLogin',
          payload: { schoolSlug, field, deploymentSlug, ssoSlug: window.location.host },
        });
        return;
      }

      const user = { ...userResult.Result };
      switch (deploymentSlug) {
        case 'ai.ksedu.cn': {
          const userResponse = yield getSchoolInfoKS({ deploymentSlug, UserName: user.UserName });
          if (responseCode(userResponse) !== 0) {
            alertError(responseMessage(userResponse));
            return;
          }
          const data = responseData<{ SchoolId: string; UserRoleId: string }>(userResponse);
          if (!data) {
            alertError('用户信息获取失败，请返回重试');
            return;
          }
          if (!data.SchoolId) {
            alertError('当前账号无法获取学校信息，请联系管理员');
            return;
          }
          const slugResponse = yield getSchoolSlugbyID({ SchoolId: data.SchoolId });
          if (responseCode(slugResponse) !== 0) {
            alertError(responseMessage(slugResponse));
            return;
          }
          schoolSlug = responseData<{ schoolSlug: string }>(slugResponse)?.schoolSlug;
          if (
            Number.parseInt(data.UserRoleId, 10) !== 0 &&
            Number.parseInt(data.UserRoleId, 10) !== -4
          ) {
            user.UserType = '教师';
          }
          break;
        }
        case 'hxr.jsnje.cn':
        case 'njhxr.jsnje.cn': {
          if (schoolSlug && schoolSlug !== 'undefined') break;
          if (!user.OrgCode) {
            alertError('当前账号无法获取学校信息，请联系管理员');
            return;
          }
          const schoolResponse = yield getSchoolSlugbyOrgCode({ OrgCode: user.OrgCode });
          if (responseCode(schoolResponse) !== 0) {
            alertError(responseMessage(schoolResponse));
            return;
          }
          schoolSlug = responseData<{ schoolSlug: string }>(schoolResponse)?.schoolSlug;
          break;
        }
        default:
          alertError('未获取单点登录配置！');
          return;
      }

      if (!schoolSlug || schoolSlug === 'undefined') {
        alertError('当前用户获取学校信息失败，请返回重试');
        return;
      }
      const token = Jwt.sign(
        {
          UserName: user.UserName,
          RealName: user.RealName,
          UserType: user.UserType,
          OrgCode: user.OrgCode,
          Email: user.Email,
          QQ: user.QQ,
          ssoToken: user.ssoToken,
        },
        'user',
      );
      window.location.replace(
        getReturnURL({
          sso,
          deploymentStyle,
          schoolSlug,
          userSearch: `token=${token}`,
          field,
        }),
      );
    },

    *retryLogin({ payload }): ModelSaga {
      const retry = payload as RetryPayload;
      const result = yield getssoLoginUrl({ ...retry });
      const requestURL = responseData<string>(result);
      if (requestURL) window.location.replace(requestURL);
    },

    *njyzLogin({ payload }): ModelSaga {
      const { ssoToken, field } = payload as { ssoToken?: string; field?: string };
      const currentSession = yield getSession();
      const apiSSO = isSessionWithSso(currentSession) ? currentSession.sso : undefined;
      const deploymentStyle = isSessionWithSso(currentSession)
        ? currentSession.deploymentStyle
        : undefined;
      if (!apiSSO) {
        alertError('没有发现本站有单点登录配置，请检查跳转站点域名是否一致');
        return;
      }
      const schoolSlug = 'njyz';
      const sso = apiSSO[schoolSlug];
      if (!sso) {
        alertError('请返回重试');
        return;
      }
      const type = field === 'admin' ? '教师' : '学生';
      const validResult = yield schoolValidToken({ token: ssoToken, schoolSlug, type });
      if (responseCode(validResult) !== 0) {
        alertError(responseMessage(validResult) || 'token验证请求失败');
        return;
      }
      const data = responseData<{
        code: string;
        msg?: string;
        data?: { userId: string; name: string; schoolId: string };
      }>(validResult);
      if (!data) {
        alertError('接口 /sso/validToken.do 请求失败，没有返回数据');
        return;
      }
      if (data.code !== '200') {
        alertError(`接口请求 /sso/validToken.do 出错, ${data.msg ?? ''}`);
        return;
      }
      if (!data.data) {
        alertError('没有用户信息');
        return;
      }
      const token = Jwt.sign(
        {
          schoolSlug,
          schoolID: data.data.schoolId,
          userType: type,
          userID: data.data.userId,
          userName: data.data.name,
          ssoToken,
        },
        'user',
      );
      window.location.replace(
        getReturnURL({
          sso,
          deploymentStyle,
          schoolSlug,
          userSearch: `token=${token}`,
          field: field ?? 'web',
        }),
      );
    },

    *test(): ModelSaga {
      const schoolResponse = yield getSchoolSlugbyID({ SchoolId: '3632000377' });
      if (responseCode(schoolResponse) !== 0) {
        alertError(responseMessage(schoolResponse));
        return;
      }
      console.log(schoolResponse, 'schoolRes');
      console.log(responseData<{ schoolSlug: string }>(schoolResponse)?.schoolSlug, 'slug');
    },
  },
  reducers: {
    update(state: Record<string, unknown>, { payload }: { payload: Record<string, unknown> }) {
      return { ...state, ...payload };
    },
  },
};

export default model;
